// Playback-event buffer + upload (docs/04 analytics; server contract:
// POST /v1/player/events { events:[…] } → { accepted:n }).
//
// The playback engine (player-core) fires `onItemPlayed` when an item leaves the
// screen; app.ts turns each into a PlaybackEvent and hands it here. Events are
// persisted to the existing IndexedDB kv on every change, so a reload or power
// loss never drops what was already recorded. The buffer is capped (oldest
// dropped) so a long offline stretch can't grow unbounded.
//
// Flush is dedupe-safe: the server keys on `client_event_id`, so a batch resent
// after a timeout is harmless. A batch is only removed from the buffer once the
// server ACKNOWLEDGES it (2xx) — a failed POST keeps every event for next time.
// Persistence + POST are injected (EventStore / EventPoster) so the buffer's
// cap/batch/ack logic is unit-testable with no IndexedDB and no live server.

const MEDIA_PATH = /\/v1\/player\/media\/([^/?#]+)/;

/**
 * Parse a media id out of a `/v1/player/media/:id` url. External type=url items
 * (an http(s) page or data: doc) have no such path → null. Used as the fallback
 * for manifests that predate the additive `media_id` field.
 */
export function mediaIdFromUrl(url: string): string | null {
  return MEDIA_PATH.exec(url)?.[1] ?? null;
}

/**
 * The media ASSET id a playback event attributes to. Prefer the resolver's
 * additive `media_id` (present for every kind, so external type=url plays are
 * countable); fall back to url-parsing for old manifests. `id` is the
 * playlist-item id and is never used here. Null → the caller emits no event.
 */
export function mediaIdOf(item: { media_id?: string; url: string }): string | null {
  return item.media_id ?? mediaIdFromUrl(item.url);
}

export interface PlaybackEvent {
  client_event_id: string;
  media_id: string;
  playlist_id?: string;
  /** Unix ms the item became visible. */
  started_at: number;
  /** Actual on-screen duration in ms. */
  duration_ms: number;
}

/** Where the buffer is persisted between reloads. Backed by IndexedDB in prod. */
export interface EventStore {
  load(): Promise<PlaybackEvent[]>;
  save(events: PlaybackEvent[]): Promise<void>;
}

/** POST one batch; resolve on 2xx (batch acknowledged), reject on any failure. */
export type EventPoster = (batch: PlaybackEvent[]) => Promise<void>;

const MAX_EVENTS = 10000; // drop oldest beyond this (raised from 2000, Sprint 11)
const FLUSH_BATCH = 500; // ≤500 events per request
const FLUSH_THRESHOLD = 50; // auto-flush once the buffer reaches this
const FLUSH_INTERVAL_MS = 5 * 60 * 1000;

export class EventBuffer {
  private buf: PlaybackEvent[] = [];
  private ready: Promise<void>;
  private flushing = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private store: EventStore,
    private poster: EventPoster,
    private onError?: (e: unknown) => void,
    /** Called when the cap is exceeded and the oldest events are dropped. */
    private onOverflow?: (dropped: number) => void,
  ) {
    // Hydrate the persisted buffer up front; add()/flush() await this so no event
    // is written before the previous session's backlog is loaded.
    this.ready = this.store
      .load()
      .then((evs) => {
        this.buf = evs.slice(-MAX_EVENTS);
      })
      .catch(() => {
        this.buf = [];
      });
  }

  /** Resolves once the persisted backlog has loaded (used by tests + callers). */
  whenReady(): Promise<void> {
    return this.ready;
  }

  size(): number {
    return this.buf.length;
  }

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /** Buffer one event; persist immediately; auto-flush once the threshold is hit. */
  async add(ev: PlaybackEvent): Promise<void> {
    await this.ready;
    this.buf.push(ev);
    if (this.buf.length > MAX_EVENTS) {
      const dropped = this.buf.length - MAX_EVENTS;
      this.buf.splice(0, dropped);
      this.onOverflow?.(dropped);
    }
    await this.persist();
    if (this.buf.length >= FLUSH_THRESHOLD) void this.flush();
  }

  /**
   * Drain the buffer to the server in ≤500-event batches. Stops at the first
   * failed batch (keeping it + everything after for the next flush). Only events
   * the server acknowledged are dropped; because add() may append during an
   * await, acknowledged events are removed by id rather than by slice length.
   */
  async flush(): Promise<void> {
    await this.ready;
    if (this.flushing || this.buf.length === 0) return;
    this.flushing = true;
    try {
      while (this.buf.length > 0) {
        const batch = this.buf.slice(0, FLUSH_BATCH);
        try {
          await this.poster(batch);
        } catch (e) {
          this.onError?.(e);
          break; // keep unacknowledged events; retry next flush
        }
        const acked = new Set(batch.map((e) => e.client_event_id));
        this.buf = this.buf.filter((e) => !acked.has(e.client_event_id));
        await this.persist();
      }
    } finally {
      this.flushing = false;
    }
  }

  private async persist(): Promise<void> {
    try {
      await this.store.save(this.buf);
    } catch {
      /* best-effort; the in-memory buffer is still authoritative until next add */
    }
  }
}
